import { createContext, useContext, useState } from 'react'
import type { ReactNode } from 'react'
import {
  CONVERSATIONS_API_URL,
  CONVERSATIONS_TOKEN_STORAGE_KEY,
  RAG_TOKEN_STORAGE_KEY,
  USUARIO_STORAGE_KEY,
} from '@/config/env'

interface AuthContextType {
  isAuthenticated: boolean
  /** Quien ha entrado, para registrar quien decide. Vacio si no se sabe. */
  usuario: string
  login: (username: string, password: string) => Promise<boolean>
  logout: () => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

// La contraseña ya no vive aquí. La comprueba el servidor, que es el único
// que la conoce, y si acierta devuelve los tokens con los que el navegador
// puede llamar a la API. Sin pasar por aquí no hay token, y sin token la API
// no responde.
const LOGIN_ENDPOINT = `${CONVERSATIONS_API_URL}/api/v1/auth/app-login/`

function readStored(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    // Navegador sin almacenamiento (modo privado, cookies bloqueadas).
    return null
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  // Hay sesión si hay token guardado: es lo mismo que hace falta para que la
  // API conteste, así que no puede desincronizarse de lo que la app puede hacer.
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(
    () => Boolean(readStored(CONVERSATIONS_TOKEN_STORAGE_KEY)),
  )

  const [usuario, setUsuario] = useState<string>(
    () => readStored(USUARIO_STORAGE_KEY) ?? '',
  )

  const login = async (username: string, password: string): Promise<boolean> => {
    let response: Response
    try {
      response = await fetch(LOGIN_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      })
    } catch {
      // El servidor no contesta. No se puede distinguir de una contraseña
      // incorrecta, y ante la duda no se entra.
      return false
    }

    if (!response.ok) {
      return false
    }

    let data: { conversations_token?: string; rag_token?: string }
    try {
      data = await response.json()
    } catch {
      return false
    }

    if (!data.conversations_token) {
      return false
    }

    try {
      localStorage.setItem(CONVERSATIONS_TOKEN_STORAGE_KEY, data.conversations_token)
      if (data.rag_token) {
        localStorage.setItem(RAG_TOKEN_STORAGE_KEY, data.rag_token)
      }
      localStorage.setItem(USUARIO_STORAGE_KEY, username)
    } catch {
      // Sin almacenamiento la sesión no sobrevive a la recarga.
    }

    setUsuario(username)
    setIsAuthenticated(true)
    return true
  }

  const logout = () => {
    setUsuario('')
    setIsAuthenticated(false)
    try {
      localStorage.removeItem(CONVERSATIONS_TOKEN_STORAGE_KEY)
      localStorage.removeItem(RAG_TOKEN_STORAGE_KEY)
      localStorage.removeItem(USUARIO_STORAGE_KEY)
    } catch {
      // No había nada que borrar.
    }
  }

  return (
    <AuthContext.Provider value={{ isAuthenticated, usuario, login, logout }}>
      {children}
    </AuthContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuthContext() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuthContext must be used within an AuthProvider')
  }
  return context
}
